import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { format, subDays, subMonths, subYears, startOfMonth, endOfMonth } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import BasicDateRangePicker from '../BasicDateRangePicker/BasicDateRangePicker';

const DateRangeSelector = ({ dateRange, setDateRange, setCurrentDateLabel, isClearable = true }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [customOpen, setCustomOpen] = useState(false);
  const [customStart, setCustomStart] = useState(dayjs());
  const [customEnd, setCustomEnd] = useState(dayjs());

  const handleOpenCustomPicker = () => {
    setCustomStart(dateRange[0] ? dayjs(dateRange[0]) : dayjs().subtract(6, 'day'));
    setCustomEnd(dateRange[1] ? dayjs(dateRange[1]) : dayjs());
    setCustomOpen(true);
    handleClose();
  };

  const handleApplyCustomPicker = () => {
    setDateRange([customStart.toDate(), customEnd.toDate()]);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = () => {
    setDateRange([null, null]);
    handleClose();
  };

  const getRangeLabel = (start, end) => {
    if (!start || !end) return 'Today';

    const today = new Date();
    const todayStr = format(today, 'dd/MM/yyyy');
    const yesterdayStr = format(subDays(today, 1), 'dd/MM/yyyy');

    const startStr = format(start, 'dd/MM/yyyy');
    const endStr = format(end, 'dd/MM/yyyy');

    // Check for quick range matches
    for (const range of quickRanges) {
      const [quickStart, quickEnd] = range.action();
      const quickStartStr = format(quickStart, 'dd/MM/yyyy');
      const quickEndStr = format(quickEnd, 'dd/MM/yyyy');

      if (startStr === quickStartStr && endStr === quickEndStr) {
        return range.label;
      }
    }

    // If no quick range matches, return the formatted date range
    return `${startStr} - ${endStr}`;
  };

  const setQuickRange = (rangeFn) => {
    const [start, end] = rangeFn();
    setDateRange([start, end]);
    if (setCurrentDateLabel) {
      setCurrentDateLabel(getRangeLabel(start, end));
    }
    handleClose();
  };

  const quickRanges = [
    { label: 'Today', action: () => [new Date(), new Date()] },
    { label: 'Yesterday', action: () => [subDays(new Date(), 1), subDays(new Date(), 1)] },
    { label: 'Last 7 Days', action: () => [subDays(new Date(), 6), new Date()] },
    { label: 'Last 30 Days', action: () => [subDays(new Date(), 29), new Date()] },
    { label: 'This Month', action: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
    {
      label: 'Last Month',
      action: () => {
        const firstDayLastMonth = startOfMonth(subMonths(new Date(), 1));
        const lastDayLastMonth = endOfMonth(subMonths(new Date(), 1));
        return [firstDayLastMonth, lastDayLastMonth];
      },
    },
    {
      label: 'This Month Last Year',
      action: () => {
        const firstDayLastYear = startOfMonth(subYears(new Date(), 1));
        const lastDayLastYear = endOfMonth(subYears(new Date(), 1));
        return [firstDayLastYear, lastDayLastYear];
      },
    },
    { label: 'This Year', action: () => [new Date(new Date().getFullYear(), 0, 1), new Date()] },
    {
      label: 'Last Year',
      action: () => {
        const lastYear = new Date().getFullYear() - 1;
        return [new Date(lastYear, 0, 1), new Date(lastYear, 11, 31)];
      },
    },
    {
      label: 'Current Financial Year',
      action: () => {
        const now = new Date();
        const fyStart =
          now.getMonth() >= 3
            ? new Date(now.getFullYear(), 3, 1)
            : new Date(now.getFullYear() - 1, 3, 1);
        return [fyStart, now];
      },
    },
    {
      label: 'Last Financial Year',
      action: () => {
        const now = new Date();
        const lastFyStart =
          now.getMonth() >= 3
            ? new Date(now.getFullYear() - 1, 3, 1)
            : new Date(now.getFullYear() - 2, 3, 1);
        const lastFyEnd =
          now.getMonth() >= 3
            ? new Date(now.getFullYear(), 2, 31)
            : new Date(now.getFullYear() - 1, 2, 31);
        return [lastFyStart, lastFyEnd];
      },
    },
  ];

  // Update the label when dateRange changes
  useEffect(() => {
    if (setCurrentDateLabel) {
      setCurrentDateLabel(getRangeLabel(dateRange[0], dateRange[1]));
    }
  }, [dateRange, setCurrentDateLabel]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2, cursor: 'pointer' }}>
        <TextField
          value={
            dateRange[0] && dateRange[1]
              ? `${format(dateRange[0], 'dd/MM/yyyy')} - ${format(dateRange[1], 'dd/MM/yyyy')}`
              : ''
          }
          placeholder="Select a date range"
          onClick={handleClick}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 1 }}>
                <CalendarMonthIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  size="small"
                  sx={{ visibility: isClearable && dateRange[0] && dateRange[1] ? 'visible' : 'hidden' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          size="small"
          fullWidth
          sx={{
            cursor: 'pointer',
            '& .MuiOutlinedInput-root': {
              height: 40,
            },
            '& .MuiOutlinedInput-input': {
              fontSize: '0.85rem',
              pr: 0,
            },
          }}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: anchorEl ? anchorEl.clientWidth : '20ch',
          },
        }}
      >
        {quickRanges.map((range, index) => (
          <React.Fragment key={range.label}>
            <MenuItem onClick={() => setQuickRange(range.action)}>{range.label}</MenuItem>
            {(index === 3 || index === 8) && <Divider />}
          </React.Fragment>
        ))}
        <Divider />
        <MenuItem onClick={handleOpenCustomPicker}>Custom Range</MenuItem>
      </Menu>

      <BasicDateRangePicker
        startDate={customStart}
        endDate={customEnd}
        setStartDate={setCustomStart}
        setEndDate={setCustomEnd}
        handleApplyDateRange={handleApplyCustomPicker}
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        showTrigger={false}
      />
    </Box>
  );
};

export default DateRangeSelector;